/**
 * ManuallyAddPlayerOption View Component
 *
 * Renders a pressable row prompting the user to manually add a player.
 * Displayed in search fields when no results are found, providing an
 * entry point for the manual player creation flow (SE-3733).
 *
 * Three variants match different host contexts:
 * - "card": standalone empty state with border (default)
 * - "inline": inside modals/popovers — matches AddToListPlayerRow dimensions
 * - "search": player search list footer — matches SearchResultsItem dimensions
 * @module components/ManuallyAddPlayerOption
 */

import { UserRoundPlus } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Divider } from "@/components/atoms/Divider";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import type { TextVariant } from "@/components/atoms/types";

import { ifWeb } from "@/utils/platform";

/** Display variant for different contexts */
export type ManuallyAddPlayerVariant = "card" | "inline" | "search";

/**
 * Props for ManuallyAddPlayerOptionView component
 */
interface ManuallyAddPlayerOptionViewProps {
  /** Contextual subtitle text below the title */
  readonly subtitle: string;
  /** Callback when the option is pressed */
  readonly onPress: () => void;
  /** Whether dark theme is active */
  readonly isDark: boolean;
  /** Display variant: "card" standalone, "inline" modals/popovers, "search" player search list */
  readonly variant?: ManuallyAddPlayerVariant;
}

/** Theme key for lookup tables */
type ThemeKey = "dark" | "light";

/** Pressable styles per variant and theme */
const PRESSABLE_STYLES: Record<
  ManuallyAddPlayerVariant,
  Record<ThemeKey, string>
> = {
  card: {
    dark: "rounded-lg border border-outline-emphasis bg-surface-raised web:cursor-pointer web:hover:bg-surface-subtle active:bg-surface-subtle",
    light:
      "rounded-lg border border-surface-muted bg-surface-base web:cursor-pointer web:hover:bg-surface-raised active:bg-surface-raised",
  },
  inline: {
    dark: "web:cursor-pointer web:hover:bg-surface-subtle active:opacity-80",
    light: "web:cursor-pointer web:hover:bg-surface-subtle active:opacity-80",
  },
  search: {
    dark: "web:cursor-pointer web:hover:bg-surface-subtle active:opacity-80",
    light: "web:cursor-pointer web:hover:bg-surface-subtle active:opacity-80",
  },
};

/** Pressable padding per variant */
const PRESSABLE_PADDING: Record<ManuallyAddPlayerVariant, string> = {
  card: "px-4 py-3",
  inline: ifWeb("px-2 py-2", "px-4 py-3"),
  search: ifWeb("px-2 py-2", "py-2"),
};

/** Icon background color per theme — amber tone */
const AVATAR_BG: Record<ThemeKey, string> = {
  dark: "rgba(120,53,15,0.3)",
  light: "#fef3c7",
};

/** Icon color per theme — amber across all variants */
const ICON_COLOR: Record<ThemeKey, string> = {
  dark: "#fbbf24",
  light: "#d97706",
};

/**
 * Avatar container spacing per variant — padding + right margin as closed
 * tokens. card/inline: padding 8 (p-2), no right margin; search: padding 4
 * (p-1) + right margin 8 (mr-2) to match SearchResultsItem.
 */
const AVATAR_SPACING_CLASS: Record<ManuallyAddPlayerVariant, string> = {
  card: "p-2",
  inline: "p-2",
  search: "p-1 mr-2",
};

/** Icon size per variant — search matches SearchResultsItem Icon size="xl" */
const ICON_SIZE: Record<ManuallyAddPlayerVariant, "sm" | "xl"> = {
  card: "sm",
  inline: "sm",
  search: "xl",
};

/**
 * Title text style per variant. The title is bold at every size, so the map
 * carries the whole bundle rather than a size class plus a weight utility:
 * `body-bold` is 14px/20px SatoshiBold (what `text-sm` + the old weight class
 * resolved to) and `title-lg` is 20px/28px SatoshiBold (what `text-xl` did).
 */
const TITLE_VARIANT: Record<ManuallyAddPlayerVariant, TextVariant> = {
  card: "body-bold",
  inline: "body-bold",
  search: "title-lg",
};

/** Subtitle text size per variant */
const SUBTITLE_SIZE: Record<ManuallyAddPlayerVariant, string> = {
  card: "text-2xs",
  inline: "text-2xs",
  search: "text-title",
};

/** HStack gap per variant */
const GAP_CLASS: Record<ManuallyAddPlayerVariant, string> = {
  card: "gap-3",
  inline: "gap-3",
  search: "gap-4",
};

/** Whether to show a top divider */
const SHOW_DIVIDER: Record<ManuallyAddPlayerVariant, boolean> = {
  card: false,
  inline: false,
  search: true,
};

/**
 * Resolves the pressable className from variant and theme
 * @param variant - Display variant to resolve styles for
 * @param isDark - Whether dark theme is active
 */
const getPressableClass = (
  variant: ManuallyAddPlayerVariant,
  isDark: boolean
): string =>
  `${PRESSABLE_PADDING[variant]} ${PRESSABLE_STYLES[variant][isDark ? "dark" : "light"]}`;

/**
 * Resolves the wrapper className based on variant
 * @param variant - Display variant to resolve wrapper spacing for
 */
const getWrapperClass = (variant: ManuallyAddPlayerVariant): string =>
  variant === "card" ? "mx-2 mt-2" : "";

/**
 * Resolves the theme key from the isDark flag
 * @param isDark - Whether dark theme is active
 */
const getThemeKey = (isDark: boolean): ThemeKey => (isDark ? "dark" : "light");

/* design-system: dynamic-style waiver — data-driven amber avatar background from AVATAR_BG theme lookup; no closed semantic token for this amber */
const avatarBgStyle = (isDark: boolean) =>
  ({
    backgroundColor: AVATAR_BG[getThemeKey(isDark)],
  }) as const;

/**
 * Pure view component for the "Manually Add Player" entry point
 * @param props - Component props
 * @param props.subtitle - Contextual subtitle (e.g. "Can't find who you're looking for?")
 * @param props.onPress - Callback when pressed
 * @param props.isDark - Dark theme flag
 * @param props.variant - Display variant ("card" standalone, "inline" modals, "search" player search)
 */
const ManuallyAddPlayerOptionView = ({
  subtitle,
  onPress,
  isDark,
  variant = "card",
}: ManuallyAddPlayerOptionViewProps) => (
  <Box className={getWrapperClass(variant)}>
    {SHOW_DIVIDER[variant] ? (
      <Divider className="bg-surface-muted opacity-50" />
    ) : null}
    <Pressable
      onPress={onPress}
      testID="manually-add-player-option"
      accessibilityRole="button"
      accessibilityLabel="Manually Add Player"
      className={getPressableClass(variant, isDark)}
    >
      <Stack
        direction="horizontal"
        className={`items-center ${GAP_CLASS[variant]}`}
      >
        <Box
          className={`items-center justify-center rounded-full ${AVATAR_SPACING_CLASS[variant]}`}
          // eslint-disable-next-line no-restricted-syntax -- data-driven amber avatar background from AVATAR_BG theme lookup; no closed semantic token for this amber
          UNSAFE_style={avatarBgStyle(isDark)}
        >
          <Icon
            as={UserRoundPlus}
            size={ICON_SIZE[variant]}
            color={ICON_COLOR[getThemeKey(isDark)]}
          />
        </Box>

        <Stack className="min-w-0 flex-1">
          <Text
            variant={TITLE_VARIANT[variant]}
            className={isDark ? "text-white" : "text-content-primary"}
            {...({ numberOfLines: 1 } as Record<string, number>)}
          >
            Manually Add Player
          </Text>
          <Text
            variant="unstyled"
            className={`${SUBTITLE_SIZE[variant]} text-content-muted`}
          >
            {subtitle}
          </Text>
        </Stack>
      </Stack>
    </Pressable>
  </Box>
);

ManuallyAddPlayerOptionView.displayName = "ManuallyAddPlayerOptionView";

export default memo(ManuallyAddPlayerOptionView);
